import { useEffect, useState } from "react";
import usePosOrders from "../../../services/Pos/usePosOrders";
import PosOrderDetailsModal from "./PosOrderDetailsModal";
import { toast } from "react-toastify";

export default function ViewPosSearchBillModal({
  open,
  onClose,
  onEdit,
  onSettle,
  onOrderCompleted,
}) {
  const [billNo, setBillNo] = useState("");
  const [searched, setSearched] = useState(false);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);

  const {
    getOrderDetails,
    updatePosOrder,
    orderDetails,
    loading,
    error,
  } = usePosOrders();

  useEffect(() => {
    if (orderDetails && searched) {
      setOrderDetailsOpen(true);
    }
  }, [orderDetails, searched]);

  useEffect(() => {
    if (!open) {
      setBillNo("");
      setSearched(false);
      setOrderDetailsOpen(false);
      setMarkingComplete(false);
    }
  }, [open]);

  if (!open) return null;

  const getOrderId = (order) => {
    const finalOrder = Array.isArray(order) ? order[0] : order;

    return (
      finalOrder?.orderid ||
      finalOrder?.orderId ||
      finalOrder?.order_id ||
      billNo.trim() ||
      ""
    );
  };

  const handleSearch = async () => {
    const finalBillNo = billNo.trim();

    if (!finalBillNo) {
      toast.error("Please enter bill number");
      return;
    }

    setSearched(true);

    const response = await getOrderDetails(finalBillNo);

    const finalOrder = Array.isArray(response)
      ? response[0]
      : response;

    if (finalOrder) {
      setOrderDetailsOpen(true);
    }
  };

  const handleMarkAsComplete = async (order) => {
    const finalOrderId = getOrderId(order);

    if (!finalOrderId) {
      toast.error("Order ID missing");
      return;
    }

    try {
      setMarkingComplete(true);

      const response = await updatePosOrder({
        orderId: finalOrderId,
      });

      if (response?.responseCode !== "00") {
        toast.error(
          response?.responseMessage ||
            "Unable to mark order as complete",
        );
        return;
      }

      toast.success(
        response?.responseMessage ||
          "Order marked as complete",
      );

      await onOrderCompleted?.(finalOrderId);

      setOrderDetailsOpen(false);
      onClose?.();
    } catch (error) {
      console.error("Mark complete error:", error);
      toast.error("Unable to mark order as complete");
    } finally {
      setMarkingComplete(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <>
  
      {!orderDetailsOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center montserrat-font-1">
          <div className="bg-white w-[900px] rounded-xl flex overflow-hidden shadow-xl montserrat-font-1">
            <div className="flex-1 p-6 montserrat-font-1">
              <div className="bg-gray-100 p-6 rounded-xl text-center">
                <h2 className="text-xl font-bold mb-6">
                  Search Order by Bill Number
                </h2>

                <div className="flex justify-center gap-3">
                  <input
                    value={billNo}
                    onChange={(event) => {
                      setBillNo(event.target.value);
                      setSearched(false);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter Bill Number"
                    className="border px-4 py-3 rounded-lg w-72 text-center text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                  />

                  <button
                    type="button"
                    onClick={handleSearch}
                    disabled={loading || !billNo.trim()}
                    className="bg-emerald-400 hover:bg-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition"
                  >
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>

                {searched && !loading && error && (
                  <p className="mt-4 text-red-500 text-sm">
                    {error}
                  </p>
                )}

                {searched &&
                  !loading &&
                  !error &&
                  !orderDetails && (
                    <p className="mt-4 text-gray-500 text-sm">
                      No order found for bill number{" "}
                      <strong>{billNo}</strong>.
                    </p>
                  )}
              </div>
            </div>

            <div className="w-64 bg-gray-100 flex flex-col justify-center montserrat-font-1 gap-4 p-6 border-l border-dashed border-gray-300">
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-300 hover:bg-gray-400 active:scale-95 transition-all py-4 rounded-lg font-bold"
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}

      <PosOrderDetailsModal
        open={orderDetailsOpen}
        onClose={onClose}
        order={orderDetails}
        onEdit={onEdit}
        onSettle={onSettle}
        onMarkAsComplete={handleMarkAsComplete}
        markingComplete={markingComplete}
      />
    </>
  );
}